import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';
import PaceSparkline from './PaceSparkline';
import PickingHistory from './PickingHistory';

const STAGE_CONFIG = {
  picking: {
    eyebrow: 'Picking',
    title: 'Remaining picking stops',
    icon: 'mdi mdi-cart-arrow-down',
    behindMessage: 'PICKING NEEDS HELP',
    unit: 'stops remaining',
    chartUnit: 'stops',
    comparisonUnit: 'stops',
    suffix: '',
  },
  packing: {
    eyebrow: 'Inspection/Packing',
    title: 'Awaiting inspection or packing',
    icon: 'mdi mdi-package-variant-closed',
    behindMessage: 'INSPECTION/PACKING NEEDS HELP',
    unit: 'orders ready',
    chartUnit: 'progress',
    comparisonUnit: 'points',
    suffix: '',
    thresholds: { warningPoints: 5, criticalPoints: 10 },
  },
  shipping: {
    eyebrow: 'Shipping',
    title: 'Waiting to ship',
    icon: 'mdi mdi-truck-fast-outline',
    behindMessage: 'SHIPPING NEEDS HELP',
    unit: 'orders waiting',
    chartUnit: 'progress',
    comparisonUnit: 'points',
    suffix: '',
    thresholds: { warningPoints: 10, criticalPoints: 20 },
  },
};

function comparisonLabel(metric, config, t) {
  if (config.comparisonUnit === 'stops') {
    if (metric.current === 0) return t('All stops picked');
    if (metric.status === 'behind') return t('High picking queue');
    if (metric.status === 'neutral') return t('Moderate picking queue');
    return t('Low picking queue');
  }

  if (metric.status === 'behind') {
    return t(`{{count}} ${config.comparisonUnit} behind`, { count: metric.difference });
  }
  if (metric.status === 'ahead') {
    return t(`{{count}} ${config.comparisonUnit} ahead`, { count: metric.difference });
  }
  if (metric.status === 'neutral') {
    return t(`{{count}} ${config.comparisonUnit} below pace`, { count: metric.difference });
  }

  return t('On pace');
}

function benchmarkDetail(stage, metric, t) {
  const progress = metric.progress ?? metric.current;

  if (stage === 'picking') {
    return t('Goal: 0 · Green < {{warning}} · Yellow {{warning}}–{{yellowMax}} · Red {{critical}}+', {
      warning: metric.thresholds.warningStops,
      yellowMax: metric.thresholds.criticalStops - 1,
      critical: metric.thresholds.criticalStops,
    });
  }

  if (stage === 'packing') {
    return t('{{packed}} packed · {{ready}} ready · 8-wk {{average}}% / cur {{progress}}%', {
      packed: metric.packed,
      ready: metric.ready,
      average: metric.average,
      progress,
    });
  }

  return t('{{shipped}} shipped · {{waiting}} waiting · 8-wk {{average}}% / cur {{progress}}%', {
    shipped: metric.shipped,
    waiting: metric.waiting,
    average: metric.average,
    progress,
  });
}

function PaceWidget({ refreshKey, stage, t }) {
  const [metric, setMetric] = useState(null);
  const [measuredAt, setMeasuredAt] = useState(null);
  const [error, setError] = useState(false);
  const config = STAGE_CONFIG[stage];

  useEffect(() => {
    let mounted = true;

    async function loadMetric() {
      try {
        const response = await get('/api/today/pace');
        if (mounted) {
          setMetric(response.metrics?.[stage] || null);
          setMeasuredAt(response.generated_at);
          setError(false);
        }
      } catch (requestError) {
        if (mounted) setError(true);
      }
    }

    loadMetric();
    return () => {
      mounted = false;
    };
  }, [refreshKey, stage]);

  const status = error ? 'error' : (metric?.status || 'loading');
  const unavailableLabel = stage === 'picking' ? 'Picking queue unavailable' : 'Benchmark unavailable';
  const loadingLabel = stage === 'picking' ? 'Loading picking queue…' : 'Loading 8-week benchmark…';

  return (
    <div className={`today-pace-card today-pace-card--${stage} today-pace-card--${status}`}>
      <div className="today-pace-card__stage">
        <div>
          <span>{t(config.eyebrow)}</span>
          <h2>{t(config.title)}</h2>
        </div>
      </div>

      <div className="today-pace-card__stage-watermark" aria-hidden="true">
        <i className={config.icon} />
        <strong>{t(config.eyebrow)}</strong>
      </div>

      {metric && !error && stage === 'picking' && (
        <PickingHistory
          measuredAt={measuredAt}
          status={metric.status}
          timeline={metric.timeline}
          t={t}
        />
      )}
      {metric && !error && stage !== 'picking' && (
        <PaceSparkline
          label={t(config.eyebrow)}
          timeline={metric.timeline}
          thresholds={config.thresholds}
          unit={config.chartUnit}
        />
      )}

      <div className="today-pace-card__body">
        <strong className="today-pace-card__queue">
          {error ? '—' : (metric?.current ?? '…')}
          {!error && metric && config.suffix}
        </strong>
        <span className="today-pace-card__queue-label">{t(config.unit)}</span>
      </div>

      <div className="today-pace-card__benchmark">
        {metric && !error ? (
          <>
            <strong>
              {metric.status === 'behind'
                ? t(config.behindMessage)
                : comparisonLabel(metric, config, t)}
            </strong>
            {metric.status === 'behind' && <span>{comparisonLabel(metric, config, t)}</span>}
            <small>{benchmarkDetail(stage, metric, t)}</small>
          </>
        ) : (
          <small>{t(error ? unavailableLabel : loadingLabel)}</small>
        )}
      </div>
    </div>
  );
}

PaceWidget.defaultProps = {
  t: (value) => value,
};

PaceWidget.propTypes = {
  refreshKey: PropTypes.number.isRequired,
  stage: PropTypes.oneOf(Object.keys(STAGE_CONFIG)).isRequired,
  t: PropTypes.func,
};

export default withTranslation()(PaceWidget);
